import * as XLSX from 'xlsx';

// TZ-независимый разбор даты из ячейки Excel.
// Числовой серийный номер разбираем через XLSX.SSF.parse_date_code (без Date/таймзоны) —
// иначе создание JS Date + локальные геттеры дают сдвиг на ±1 день. Файлы читаем с
// cellDates:false, чтобы настоящие даты приходили числами и попадали в эту ветку.
export function excelToISO(v) {
  const pad = (n) => String(n).padStart(2, '0');
  if (v == null || v === '') return null;
  if (typeof v === 'number') {
    const dc = XLSX.SSF.parse_date_code(v);
    if (!dc || !dc.y) return null;
    return `${dc.y}-${pad(dc.m)}-${pad(dc.d)}`;
  }
  if (v instanceof Date) {
    // подстраховка (при cellDates:false настоящие даты сюда не попадают)
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);            // YYYY-MM-DD
  if (m) return `${m[1]}-${pad(+m[2])}-${pad(+m[3])}`;
  m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);          // DD.MM.YYYY / DD/MM/YYYY
  if (m) return `${m[3]}-${pad(+m[2])}-${pad(+m[1])}`;
  return null;
}

// Merchandisers может прийти как массив или как объект (зависит от unique constraint в БД)
function getMrc(route) {
  const m = route.merchandisers;
  if (!m) return null;
  return Array.isArray(m) ? m[0] : m;
}

// Цвета для недель
const WEEK_COLORS = {
  36: { hdr: '2E75B6', light: 'D6E4F0' },
  37: { hdr: '70AD47', light: 'E2EFDA' },
  38: { hdr: 'FFC000', light: 'FFF2CC' },
  39: { hdr: 'F4B183', light: 'FCE4D6' },
  40: { hdr: '808080', light: 'EDEDED' },
};

const FIXED_HDR_COLOR = '1F3864';
const MONTH_HDR_COLOR = '243F60';

// Недели сентября 2026
function getWeeks(year, month) {
  const weeks = {};
  const daysInMonth = new Date(year, month, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(year, month - 1, d);
    const w = getISOWeek(dt);
    if (!weeks[w]) weeks[w] = [];
    weeks[w].push({ day: d, dow: dt.getDay() }); // 0=вс, 1=пн...
  }
  return weeks;
}

function getISOWeek(dt) {
  const d = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function isWeekend(dow) { return dow === 0 || dow === 6; } // 0=вс, 6=сб

function dayLabel(d, month) {
  const abbr = ['', 'янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'][month];
  return d <= 9 ? `${d} ${abbr}` : `${d}.${abbr}`;
}

const DOW_RU = ['вс','пн','вт','ср','чт','пт','сб'];

// Статусы которые считаются выходами
const EXIT_STATUSES = new Set(['1', 'СВ']);
const OVERLAP_STATUSES = new Set(['БЛ+МР','БЛ+ПМ','БЛ+ВЫХ','ОТ+МР','ОТ+ПМ','ОТ+ВЫХ','ДО+МР','ДО+ПМ','ДО+ВЫХ','ВК+МР','ВК+ПМ','ВК+ВЫХ']);

function cellStyle(fgColor, fontColor = '000000', bold = false) {
  return {
    fill: { fgColor: { rgb: fgColor }, patternType: 'solid' },
    font: { color: { rgb: fontColor }, bold, sz: 9, name: 'Arial' },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top:    { style: 'thin', color: { rgb: 'BFBFBF' } },
      bottom: { style: 'thin', color: { rgb: 'BFBFBF' } },
      left:   { style: 'thin', color: { rgb: 'BFBFBF' } },
      right:  { style: 'thin', color: { rgb: 'BFBFBF' } },
    },
  };
}

/**
 * Генерирует Excel-файл в формате табеля
 * @param {Object} params
 * @param {Array}  params.routes     — маршруты с МРЧ
 * @param {Object} params.statuses   — { route_id: { 'YYYY-MM-DD': status_code } }
 * @param {number} params.year
 * @param {number} params.month
 * @param {string} params.sheetName  — название листа
 * @param {string} params.fileName   — имя файла без расширения
 */
export function exportTabellToExcel({ routes, statuses, year, month, sheetName, fileName }) {
  const wb = XLSX.utils.book_new();
  const ws = {};
  const weeks = getWeeks(year, month);
  const weekNums = Object.keys(weeks).map(Number).sort((a, b) => a - b);

  const FIXED_HEADERS = [
    'ТМ', 'BU', 'SG', 'Город', 'SV внутренний код', 'SV код', 'SV ФИО',
    'Маршрут', 'Код', 'Внутренний код маршрута', 'Тип', 'ФИО МРЧ',
    `Выходные`, 'Номер заявки ECP', 'Вес маршрута %',
  ];
  const SVC_HEADERS = weekNums.map(w => `Service W${w}%`);

  // Строим список всех колонок
  const cols = [];
  FIXED_HEADERS.forEach(h => cols.push({ type: 'fixed', header: h }));
  SVC_HEADERS.forEach((h, i) => cols.push({ type: 'service', header: h, week: weekNums[i] }));

  weekNums.forEach(w => {
    const days = weeks[w];
    days.forEach(({ day, dow }) => {
      cols.push({ type: 'day', header: dayLabel(day, month), day, dow, week: w });
    });
    cols.push({ type: 'summary', header: `Итог выходов W${w}`,      week: w, metric: 'exits' });
    cols.push({ type: 'summary', header: `Кол-во больничных W${w}`, week: w, metric: 'sick' });
    cols.push({ type: 'summary', header: `Кол-во вакансий W${w}`,   week: w, metric: 'vacancy' });
    cols.push({ type: 'summary', header: `Кол-во отпусков W${w}`,   week: w, metric: 'vacation' });
    cols.push({ type: 'summary', header: `Кол-во ДО W${w}`,         week: w, metric: 'do' });
    cols.push({ type: 'summary', header: `Кол-во перекрытий W${w}`, week: w, metric: 'overlaps' });
  });

  const monthName = ['','Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'][month];
  cols.push({ type: 'month', header: `Итог выходов ${monthName}`,        metric: 'exits' });
  cols.push({ type: 'month', header: `Итог невыходов БЛ ${monthName}`,   metric: 'sick' });
  cols.push({ type: 'month', header: `Итог невыходов ВК ${monthName}`,   metric: 'vacancy' });
  cols.push({ type: 'month', header: `Итог невыходов ОТ ${monthName}`,   metric: 'vacation' });
  cols.push({ type: 'month', header: `Итог невыходов ДО ${monthName}`,   metric: 'do' });
  cols.push({ type: 'month', header: `Итог перекрытий ${monthName}`,     metric: 'overlaps' });

  const totalCols = cols.length;

  // Легенда строки 1-2
  ws['A1'] = { v: 'Вакансия - ВК-', s: cellStyle('FFFFFF') };
  ws['G1'] = { v: 'Отпуск - ОТ-',   s: cellStyle('FFFFFF') };
  ws['H1'] = { v: 'ДО - ДО-',       s: cellStyle('FFFFFF') };
  ws['A2'] = { v: 'Выход - 1 / СВ', s: cellStyle('FFFFFF') };
  ws['G2'] = { v: 'Больничный - БЛ-',s: cellStyle('FFFFFF') };
  ws['H2'] = { v: 'Перекрытие - +МР/+ПМ/+ВЫХ', s: cellStyle('FFFFFF') };

  // Строка 3: дни недели для day-колонок
  cols.forEach((col, ci) => {
    if (col.type === 'day') {
      const addr = XLSX.utils.encode_cell({ r: 2, c: ci });
      const wc = WEEK_COLORS[col.week] || {};
      const bg = isWeekend(col.dow) ? (wc.light || 'EEEEEE') : (wc.light || 'FFFFFF');
      ws[addr] = { v: DOW_RU[col.dow].toUpperCase(), s: cellStyle(bg, '555555', true) };
    }
  });

  // Строка 4: заголовки
  cols.forEach((col, ci) => {
    const addr = XLSX.utils.encode_cell({ r: 3, c: ci });
    const { type, week } = col;
    let bg = 'FFFFFF', fg = '000000', bold = true;

    if (type === 'fixed') { bg = FIXED_HDR_COLOR; fg = 'FFFFFF'; }
    else if (type === 'service') { bg = '2F5496'; fg = 'FFFFFF'; }
    else if (type === 'day') {
      const wc = WEEK_COLORS[week] || {};
      bg = isWeekend(col.dow) ? (wc.light || 'EEEEEE') : (wc.light || 'FFFFFF');
    }
    else if (type === 'summary') {
      const wc = WEEK_COLORS[week] || {};
      bg = wc.hdr || '2E75B6'; fg = 'FFFFFF';
    }
    else if (type === 'month') { bg = MONTH_HDR_COLOR; fg = 'FFFFFF'; }

    ws[addr] = { v: col.header, s: cellStyle(bg, fg, bold) };
  });

  // Строки данных (5+)
  routes.forEach((route, ri) => {
    const rowIdx = 4 + ri;
    const routeStatuses = statuses[route.id] || {};
    const mrc = getMrc(route) || {};
    const sv = route.supervisors || {};

    // Считаем метрики по неделям
    const weekMetrics = {};
    weekNums.forEach(w => {
      const m = { exits: 0, sick: 0, vacancy: 0, vacation: 0, do: 0, overlaps: 0 };
      weeks[w].forEach(({ day, dow }) => {
        const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
        const s = routeStatuses[dateStr];
        if (!s) return;
        if (EXIT_STATUSES.has(s)) m.exits++;
        if (s === 'БЛ-') m.sick++;
        if (s === 'ВК-') m.vacancy++;
        if (s === 'ОТ-') m.vacation++;
        if (s === 'ДО-') m.do++;
        if (OVERLAP_STATUSES.has(s)) m.overlaps++;
      });
      weekMetrics[w] = m;
    });

    // Месячные итоги
    const monthMetrics = { exits: 0, sick: 0, vacancy: 0, vacation: 0, do: 0, overlaps: 0 };
    weekNums.forEach(w => {
      Object.keys(monthMetrics).forEach(k => { monthMetrics[k] += weekMetrics[w][k]; });
    });

    cols.forEach((col, ci) => {
      const addr = XLSX.utils.encode_cell({ r: rowIdx, c: ci });
      const { type, week, day, metric, dow } = col;
      let v = '', s = cellStyle('FFFFFF');

      if (type === 'fixed') {
        const fixedVals = [
          sv.tm_name || '', route.bu || '', route.sg || '', route.city || '',
          sv.internal_code || '', sv.external_code || '', sv.full_name || '',
          route.external_code || '', route.short_code || '', route.internal_code || '',
          route.route_type || '', mrc.full_name || '',
          mrc.weekends || '', mrc.vacancy_request || '', route.weight_sg_pct || '',
        ];
        v = fixedVals[FIXED_HEADERS.indexOf(col.header)] ?? '';
        s = { ...cellStyle('FFFFFF'), alignment: { horizontal: 'left', vertical: 'center', wrapText: true } };
      }
      else if (type === 'service') { v = ''; }
      else if (type === 'day') {
        const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
        v = routeStatuses[dateStr] || '';
        const wc = WEEK_COLORS[week] || {};
        const bg = isWeekend(dow) ? (wc.light || 'F0F0F0') : 'FFFFFF';
        // Цвет по статусу
        const statusColors = {
          '1': 'C6EFCE', 'СВ': 'BDD7EE', 'ПЛ ВЫХ': 'EDEDED',
          'БЛ-': 'FFC7CE', 'ОТ-': 'FFE699', 'ДО-': 'FCE4D6', 'ВК-': 'E2EFDA',
        };
        const cellBg = v ? (statusColors[v] || (OVERLAP_STATUSES.has(v) ? 'D6BCF5' : bg)) : bg;
        s = cellStyle(cellBg, '000000', false);
      }
      else if (type === 'summary') {
        v = weekMetrics[week]?.[metric] ?? 0;
        const wc = WEEK_COLORS[week] || {};
        s = cellStyle(wc.light || 'F0F0F0', '000000', true);
      }
      else if (type === 'month') {
        v = monthMetrics[metric] ?? 0;
        s = cellStyle('D6E4F0', '000000', true);
      }

      ws[addr] = { v, s };
    });
  });

  // Диапазон листа
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: 4 + routes.length - 1, c: totalCols - 1 },
  });

  // Ширина колонок
  ws['!cols'] = cols.map(col => {
    if (col.type === 'fixed') return { wch: col.header.length > 15 ? 20 : 12 };
    if (col.type === 'day')   return { wch: 5 };
    if (col.type === 'summary' || col.type === 'month') return { wch: 14 };
    return { wch: 10 };
  });

  // Freeze: строки 1-4 + первые 15 колонок
  ws['!freeze'] = { xSplit: 15, ySplit: 4, topLeftCell: 'P5', activePane: 'bottomRight' };

  XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Табель');
  XLSX.writeFile(wb, `${fileName || 'tabel'}.xlsx`);
}


/**
 * Читает файл отпусков (CSV или Excel) и возвращает массив строк для сентября
 * Поддерживает два формата:
 * - Ячейки "1" / "1,00" в каждом дне отпуска
 * - Диапазон "01.09-13.09" в одной ячейке
 */
export function parseVacationFile(file, year = 2026, month = 9) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    const stripQuotes = (s) => (s || '').trim().replace(/^"(.*)"$/, '$1').trim();

    const processText = (text) => {
      try {
        // Определяем разделитель (CSV с ; или ,)
        const firstLines = text.split(/\r?\n/).slice(0, 5).join('\n');
        const semiCount = (firstLines.match(/;/g) || []).length;
        const commaCount = (firstLines.match(/,/g) || []).length;
        const sep = semiCount >= commaCount ? ';' : ',';

        // Простой CSV-парсер с поддержкой кавычек
        function parseCSVLine(line) {
          const result = [];
          let cur = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
              if (inQuotes && line[i+1] === '"') { cur += '"'; i++; }
              else inQuotes = !inQuotes;
            } else if (ch === sep && !inQuotes) {
              result.push(cur); cur = '';
            } else {
              cur += ch;
            }
          }
          result.push(cur);
          return result;
        }

        const rawRows = text.split(/\r?\n/).filter(r => r.trim().length > 0);
        const rows = rawRows.map(parseCSVLine).map(row => row.map(stripQuotes));
        if (rows.length < 2) { resolve([]); return; }

        // Регулярка для дат: DD.MM.YYYY или DD-MM-YYYY, возможны кавычки уже убраны
        const dateRe = /^(\d{1,2})[.\-](\d{1,2})[.\-](\d{4})$/;

        const parseDate = (s) => {
          const str = stripQuotes(s).replace(',', '.');
          const m = str.match(dateRe);
          if (!m) return null;
          try { return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1])); }
          catch { return null; }
        };

        // Ищем строку заголовков — первую строку среди первых 5, где есть хотя бы 3 ячейки с датами нужного месяца
        let headerRowIdx = -1;
        let monthCols = [];
        for (let ri = 0; ri < Math.min(5, rows.length); ri++) {
          const candidateCols = rows[ri].map((h, i) => {
            const d = parseDate(h);
            return (d && d.getMonth() + 1 === month && d.getFullYear() === year) ? i : -1;
          }).filter(i => i >= 0);
          if (candidateCols.length >= 3) {
            headerRowIdx = ri;
            monthCols = candidateCols;
            break;
          }
        }

        if (headerRowIdx === -1) {
          reject(new Error(`Колонки за месяц ${month}/${year} не найдены. Проверьте формат дат в файле (ожидается ДД.ММ.ГГГГ) и убедитесь, что выбран нужный период.`));
          return;
        }

        const hdrs = rows[headerRowIdx];

        // Ключевые колонки — ищем в этой же строке заголовков
        const SV_COL  = hdrs.findIndex(h => stripQuotes(h).toUpperCase().startsWith('SV') || stripQuotes(h).toUpperCase() === 'СВ');
        const MRC_COL = hdrs.findIndex(h => {
          const hl = stripQuotes(h).toLowerCase();
          return hl.includes('мрч') || hl.includes('мерч');
        });

        if (SV_COL === -1 || MRC_COL === -1) {
          reject(new Error('Не найдены колонки SV или МРЧ в файле. Проверьте структуру файла.'));
          return;
        }

        const parseRange = (s) => {
          const str = stripQuotes(s);
          const m = str.match(/(\d{1,2}[.,]\d{1,2}(?:\.\d{4})?)\s*[-–]\s*(\d{1,2}[.,]\d{1,2}(?:\.\d{4})?)/);
          if (!m) return null;
          const parseShort = (v) => {
            const vv = v.replace(',', '.');
            const mm = vv.match(/^(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?/);
            if (!mm) return null;
            try { return new Date(parseInt(mm[3] || year), parseInt(mm[2]) - 1, parseInt(mm[1])); }
            catch { return null; }
          };
          const from = parseShort(m[1]);
          const to   = parseShort(m[2]);
          return (from && to) ? { from, to } : null;
        };

        const toISO = (d) => d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : null;

        const results = [];

        for (let ri = headerRowIdx + 1; ri < rows.length; ri++) {
          const row = rows[ri];
          if (!row || row.length < 4) continue;

          const sv_code  = stripQuotes(row[SV_COL]);
          const mrc_name = stripQuotes(row[MRC_COL]);
          if (!sv_code || !mrc_name) continue;

          let rangeFrom = null, rangeTo = null;
          const days = [];

          for (const ci of monthCols) {
            const val = stripQuotes(row[ci]);
            if (!val || val.startsWith('-')) continue;

            const rng = parseRange(val);
            if (rng) {
              const monthStart = new Date(year, month - 1, 1);
              const monthEnd   = new Date(year, month - 1 + 1, 0);
              rangeFrom = rng.from < monthStart ? monthStart : rng.from;
              rangeTo   = rng.to   > monthEnd   ? monthEnd   : rng.to;
              break;
            }

            const num = parseFloat(val.replace(',', '.'));
            if (!isNaN(num) && num >= 1) {
              const hdrDate = parseDate(hdrs[ci]);
              if (hdrDate && hdrDate.getMonth() + 1 === month) days.push(hdrDate);
            }
          }

          let finalFrom = null, finalTo = null;
          if (rangeFrom && rangeTo) {
            finalFrom = rangeFrom; finalTo = rangeTo;
          } else if (days.length) {
            days.sort((a, b) => a - b);
            finalFrom = days[0]; finalTo = days[days.length - 1];
          }

          if (finalFrom && finalTo) {
            let workDays = 0;
            const cur = new Date(finalFrom);
            while (cur <= finalTo) {
              const dow = cur.getDay();
              if (dow !== 0 && dow !== 6) workDays++;
              cur.setDate(cur.getDate() + 1);
            }

            results.push({
              sv_code,
              mrc_name,
              date_from:    toISO(finalFrom),
              date_to:      toISO(finalTo),
              working_days: workDays,
            });
          }
        }

        resolve(results);
      } catch (err) {
        reject(new Error('Ошибка разбора файла: ' + err.message));
      }
    };

    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.onload = (e) => processText(e.target.result);
      reader.onerror = () => reject(new Error('Ошибка чтения файла'));
      reader.readAsText(file, 'UTF-8');
      return;
    }

    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const csv = XLSX.utils.sheet_to_csv(ws, { FS: ';' });
        processText(csv);
      } catch (err) {
        reject(new Error('Не удалось прочитать Excel: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}

/**
 * Читает Excel-файл SUPERVISOR и возвращает массив строк
 */
export function parseSupervisorFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });

        // Разбор одного листа (основной или лист ПМ — структура одинаковая).
        const parseSheet = (sheetName, isPm) => {
          const ws = wb.Sheets[sheetName];
          if (!ws) return [];
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
          if (rows.length < 2) return [];
          const hdrs = rows[0].map(v => String(v || '').trim());
          const idx = (keywords) => hdrs.findIndex(h => keywords.some(k => h.toLowerCase().includes(k.toLowerCase())));
          const cols = {
            tm: idx(['ТМ']), bu: idx(['BU']), sg: idx(['SG', 'Группа продаж']), city: idx(['Город']),
            sv_int: idx(['SV внутренний код']), sv_ext: idx(['SV код']), sv_name: idx(['SV ФИО']),
            sv_phone: idx(['SV телефон']), sv_email: idx(['SV эл']),
            route_ext: idx(['Маршрут']), route_short: idx(['Код']), route_int: idx(['Внутренний код маршрута']),
            route_type: idx(['Тип']), substitute: idx(['Подменный']),
            mrc_name: idx(['ФИО МРЧ']), mrc_phone: idx(['МРЧ Телефон']), mrc_email: idx(['МРЧ Эл']),
            hire_form: idx(['Форма оформления']), weekends: idx(['Выходные']),
            vacancy_request: idx(['Номер заявки', 'ECP', 'ЕСР']),
          };
          return rows.slice(1).map(row => {
            if (!row[cols.route_int]) return null;
            const svName = row[cols.sv_name] ? String(row[cols.sv_name]) : '';
            const mrcName = row[cols.mrc_name] ? String(row[cols.mrc_name]) : '';
            const isVacancy = !mrcName || mrcName.trim() === '' || mrcName.toLowerCase().includes('вакансия');
            return {
              tm_name: row[cols.tm] ? String(row[cols.tm]) : '',
              bu: row[cols.bu] ? String(row[cols.bu]) : '',
              sg: row[cols.sg] ? String(row[cols.sg]) : '',
              city: row[cols.city] ? String(row[cols.city]) : '',
              sv_internal_code: row[cols.sv_int] ? String(row[cols.sv_int]) : '',
              sv_external_code: row[cols.sv_ext] ? String(row[cols.sv_ext]) : '',
              sv_name: svName,
              sv_phone: row[cols.sv_phone] ? String(row[cols.sv_phone]) : '',
              sv_email: row[cols.sv_email] ? String(row[cols.sv_email]) : '',
              route_external_code: row[cols.route_ext] ? String(row[cols.route_ext]) : '',
              route_short_code: row[cols.route_short] ? String(row[cols.route_short]) : '',
              route_internal_code: String(row[cols.route_int]),
              route_type: row[cols.route_type] ? String(row[cols.route_type]) : '',
              is_substitute: !!(row[cols.substitute]),
              is_pm: !!isPm,                  // #6 — строка с листа ПМ (подменный мерч)
              is_vacancy: isPm ? false : isVacancy,   // ПМ не вакансии
              mrc_name: mrcName,
              mrc_phone: row[cols.mrc_phone] ? String(row[cols.mrc_phone]) : '',
              mrc_email: row[cols.mrc_email] ? String(row[cols.mrc_email]) : '',
              hire_form: row[cols.hire_form] ? String(row[cols.hire_form]) : '',
              weekends: row[cols.weekends] ? String(row[cols.weekends]) : '',
              vacancy_request: row[cols.vacancy_request] ? String(row[cols.vacancy_request]) : '',
            };
          }).filter(Boolean);
        };

        // Основной лист — первый. Лист ПМ — ищем по имени (ПМ / подмен).
        const mainSheet = wb.SheetNames[0];
        const pmSheet = wb.SheetNames.find(n => /пм|подмен/i.test(String(n)) && n !== mainSheet);
        const result = [
          ...parseSheet(mainSheet, false),
          ...(pmSheet ? parseSheet(pmSheet, true) : []),
        ];
        if (!result.length) { resolve([]); return; }

        resolve(result);
      } catch (err) {
        reject(new Error('Не удалось прочитать файл SUPERVISOR: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}


// ── ПОЛНАЯ ВЫГРУЗКА ПРОЕКТА (для админа/РМ) ───────────────────────────────────
// tmData: [{ tm: {full_name}, routes: [...], statuses: {...} }]

export function exportFullProjectToExcel({ tmData, year, month, fileName }) {
  const wb = XLSX.utils.book_new();

  // Лист на каждого ТМ
  tmData.forEach(({ tm, routes, statuses }) => {
    if (!routes.length) return;
    const sheetName = (tm.full_name || 'ТМ').slice(0, 28).replace(/[\\/*?:[\]]/g, '');
    appendTabellSheet(wb, { routes, statuses, year, month, sheetName });
  });

  // Сводный лист по всем ТМ
  const weeks = getWeeks(year, month);
  const weekNums = Object.keys(weeks).map(Number).sort((a,b)=>a-b);
  const wsPivot = {};
  let row = 0;

  const hdrCell = (r, c, v, bg='1F3864') => {
    const addr = XLSX.utils.encode_cell({ r, c });
    wsPivot[addr] = { v, s: cellStyle(bg, 'FFFFFF', true) };
  };
  const dataCell = (r, c, v, bg='FFFFFF') => {
    const addr = XLSX.utils.encode_cell({ r, c });
    wsPivot[addr] = { v, s: cellStyle(bg, '000000', false) };
  };

  hdrCell(row, 0, 'ТМ'); 
  weekNums.forEach((w, i) => hdrCell(row, i+1, `Выходы W${w}`));
  hdrCell(row, weekNums.length+1, 'Итого за месяц');
  row++;

  tmData.forEach(({ tm, routes, statuses }) => {
    dataCell(row, 0, tm.full_name || '', 'D6E4F0');
    let monthTotal = 0;
    weekNums.forEach((w, i) => {
      const days = weeks[w];
      let weekExits = 0;
      routes.forEach(route => {
        const rs = statuses[route.id] || {};
        days.forEach(({ day }) => {
          const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const s = rs[dateStr];
          if (s === '1' || s === 'СВ') weekExits++;
        });
      });
      dataCell(row, i+1, weekExits);
      monthTotal += weekExits;
    });
    dataCell(row, weekNums.length+1, monthTotal, 'D6E4F0');
    row++;
  });

  wsPivot['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row, c: weekNums.length+1 } });
  wsPivot['!cols'] = [{ wch: 24 }, ...weekNums.map(() => ({ wch: 12 })), { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, wsPivot, 'Сводная_ТМ');

  XLSX.writeFile(wb, `${fileName || 'Полная_выгрузка'}.xlsx`);
}

function appendTabellSheet(wb, { routes, statuses, year, month, sheetName }) {
  // Переиспользуем логику exportTabellToExcel, но добавляем в существующую книгу
  const ws = {};
  const weeks = getWeeks(year, month);
  const weekNums = Object.keys(weeks).map(Number).sort((a, b) => a - b);

  const FIXED_HEADERS = [
    'ТМ', 'BU', 'SG', 'Город', 'SV внутренний код', 'SV код', 'SV ФИО',
    'Маршрут', 'Код', 'Внутренний код маршрута', 'Тип', 'ФИО МРЧ',
    'Выходные', 'Номер заявки ECP', 'Вес маршрута %',
  ];
  const SVC_HEADERS = weekNums.map(w => `Service W${w}%`);

  const cols = [];
  FIXED_HEADERS.forEach(h => cols.push({ type: 'fixed', header: h }));
  SVC_HEADERS.forEach((h, i) => cols.push({ type: 'service', header: h, week: weekNums[i] }));

  weekNums.forEach(w => {
    const days = weeks[w];
    days.forEach(({ day, dow }) => {
      cols.push({ type: 'day', header: dayLabel(day, month), day, dow, week: w });
    });
    cols.push({ type: 'summary', header: `Итог выходов W${w}`,      week: w, metric: 'exits' });
    cols.push({ type: 'summary', header: `Кол-во больничных W${w}`, week: w, metric: 'sick' });
    cols.push({ type: 'summary', header: `Кол-во вакансий W${w}`,   week: w, metric: 'vacancy' });
    cols.push({ type: 'summary', header: `Кол-во отпусков W${w}`,   week: w, metric: 'vacation' });
    cols.push({ type: 'summary', header: `Кол-во ДО W${w}`,         week: w, metric: 'do' });
    cols.push({ type: 'summary', header: `Кол-во перекрытий W${w}`, week: w, metric: 'overlaps' });
  });

  const monthName = ['','Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'][month];
  cols.push({ type: 'month', header: `Итог выходов ${monthName}`, metric: 'exits' });
  cols.push({ type: 'month', header: `Итог невыходов БЛ ${monthName}`, metric: 'sick' });
  cols.push({ type: 'month', header: `Итог невыходов ВК ${monthName}`, metric: 'vacancy' });
  cols.push({ type: 'month', header: `Итог невыходов ОТ ${monthName}`, metric: 'vacation' });
  cols.push({ type: 'month', header: `Итог невыходов ДО ${monthName}`, metric: 'do' });
  cols.push({ type: 'month', header: `Итог перекрытий ${monthName}`, metric: 'overlaps' });

  const totalCols = cols.length;

  cols.forEach((col, ci) => {
    const addr = XLSX.utils.encode_cell({ r: 3, c: ci });
    const { type, week } = col;
    let bg = 'FFFFFF', fg = '000000';
    if (type === 'fixed') { bg = FIXED_HDR_COLOR; fg = 'FFFFFF'; }
    else if (type === 'service') { bg = '2F5496'; fg = 'FFFFFF'; }
    else if (type === 'day') { const wc = WEEK_COLORS[week] || {}; bg = isWeekend(col.dow) ? (wc.light||'EEE') : (wc.light||'FFF'); }
    else if (type === 'summary') { const wc = WEEK_COLORS[week] || {}; bg = wc.hdr || '2E75B6'; fg = 'FFFFFF'; }
    else if (type === 'month') { bg = MONTH_HDR_COLOR; fg = 'FFFFFF'; }
    ws[addr] = { v: col.header, s: cellStyle(bg, fg, true) };
  });

  routes.forEach((route, ri) => {
    const rowIdx = 4 + ri;
    const routeStatuses = statuses[route.id] || {};
    const mrc = getMrc(route) || {};
    const sv = route.supervisors || {};

    const weekMetrics = {};
    weekNums.forEach(w => {
      const m = { exits: 0, sick: 0, vacancy: 0, vacation: 0, do: 0, overlaps: 0 };
      weeks[w].forEach(({ day }) => {
        const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
        const s = routeStatuses[dateStr];
        if (!s) return;
        if (EXIT_STATUSES.has(s)) m.exits++;
        if (s === 'БЛ-') m.sick++;
        if (s === 'ВК-') m.vacancy++;
        if (s === 'ОТ-') m.vacation++;
        if (s === 'ДО-') m.do++;
        if (OVERLAP_STATUSES.has(s)) m.overlaps++;
      });
      weekMetrics[w] = m;
    });

    const monthMetrics = { exits: 0, sick: 0, vacancy: 0, vacation: 0, do: 0, overlaps: 0 };
    weekNums.forEach(w => Object.keys(monthMetrics).forEach(k => { monthMetrics[k] += weekMetrics[w][k]; }));

    cols.forEach((col, ci) => {
      const addr = XLSX.utils.encode_cell({ r: rowIdx, c: ci });
      const { type, week, day, metric, dow } = col;
      let v = '', s = cellStyle('FFFFFF');

      if (type === 'fixed') {
        const fixedVals = [
          sv.tm_name || '', route.bu || '', route.sg || '', route.city || '',
          sv.internal_code || '', sv.external_code || '', sv.full_name || '',
          route.external_code || '', route.short_code || '', route.internal_code || '',
          route.route_type || '', mrc.full_name || '',
          mrc.weekends || '', mrc.vacancy_request || '', route.weight_sg_pct || '',
        ];
        v = fixedVals[FIXED_HEADERS.indexOf(col.header)] ?? '';
      } else if (type === 'day') {
        const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
        v = routeStatuses[dateStr] || '';
        const wc = WEEK_COLORS[week] || {};
        const bg = isWeekend(dow) ? (wc.light || 'F0F0F0') : 'FFFFFF';
        const statusColors = { '1':'C6EFCE','СВ':'BDD7EE','ПЛ ВЫХ':'EDEDED','БЛ-':'FFC7CE','ОТ-':'FFE699','ДО-':'FCE4D6','ВК-':'E2EFDA' };
        s = cellStyle(v ? (statusColors[v] || (OVERLAP_STATUSES.has(v) ? 'D6BCF5' : bg)) : bg);
      } else if (type === 'summary') {
        v = weekMetrics[week]?.[metric] ?? 0;
        const wc = WEEK_COLORS[week] || {};
        s = cellStyle(wc.light || 'F0F0F0', '000000', true);
      } else if (type === 'month') {
        v = monthMetrics[metric] ?? 0;
        s = cellStyle('D6E4F0', '000000', true);
      }
      ws[addr] = { v, s };
    });
  });

  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 4 + routes.length - 1, c: totalCols - 1 } });
  ws['!cols'] = cols.map(col => col.type==='fixed' ? {wch: col.header.length>15?20:12} : col.type==='day' ? {wch:5} : {wch:14});

  XLSX.utils.book_append_sheet(wb, ws, sheetName || 'ТМ');
}


// ── ПАРСЕР ФАЙЛА SERVICE% ─────────────────────────────────────────────────────
// Формат: TM Name, SV Name, ROUTE_NAME, ISO Weeknumber, Visits Plan, Visits Fact, Visits Service%

export function parseServicePctFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (rows.length < 2) { resolve([]); return; }

        const hdrs = rows[0].map(h => String(h || '').trim());
        const idx = (names) => hdrs.findIndex(h => names.some(n => h.toLowerCase().includes(n.toLowerCase())));

        const cols = {
          tm:     idx(['TM Name', 'TM']),
          sv:     idx(['SV Name', 'SV']),
          route:  idx(['ROUTE_NAME', 'Route']),
          week:   idx(['Weeknumber', 'Week']),
          plan:   idx(['Visits Plan', 'Plan']),
          fact:   idx(['Visits Fact', 'Fact']),
          pct:    idx(['Service%', 'Service %', 'Service']),
        };

        const result = rows.slice(1).map(row => {
          if (!row[cols.route]) return null;
          return {
            route_external_code: String(row[cols.route]),
            week_number:         parseInt(row[cols.week]) || null,
            visits_plan:         row[cols.plan] != null ? parseFloat(row[cols.plan]) : null,
            visits_fact:         row[cols.fact] != null ? parseFloat(row[cols.fact]) : null,
            service_pct:         row[cols.pct] != null ? parseFloat(row[cols.pct]) : null,
          };
        }).filter(r => r && r.week_number && r.service_pct != null);

        resolve(result);
      } catch (err) {
        reject(new Error('Не удалось прочитать файл Service%: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}


// ── ПАРСЕР ФАЙЛА ФАКТА ВИЗИТОВ (СВЕРКА) ───────────────────────────────────────
// Формат: строка 1 = фильтры (игнорируем), строка 3 = заголовки:
// TM Name, SV Name, ROUTE_NAME, Date, Visits Plan, Visits Fact, Visits Service%

export function parseVisitFactsFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Ищем строку заголовков (содержит ROUTE_NAME)
        let headerRow = -1;
        for (let i = 0; i < Math.min(6, rows.length); i++) {
          const r = (rows[i] || []).map(v => String(v || ''));
          if (r.some(v => v.includes('ROUTE_NAME'))) { headerRow = i; break; }
        }
        if (headerRow === -1) { reject(new Error('Не найдена строка заголовков (ROUTE_NAME)')); return; }

        const hdrs = rows[headerRow].map(h => String(h || '').trim());
        const idx = (names) => hdrs.findIndex(h => names.some(n => h.toLowerCase().includes(n.toLowerCase())));

        const cols = {
          route: idx(['ROUTE_NAME']),
          date:  idx(['Date']),
          plan:  idx(['Visits Plan']),
          fact:  idx(['Visits Fact']),
          pct:   idx(['Service%', 'Service']),
        };

        const toISO = excelToISO;

        const result = rows.slice(headerRow + 1).map(row => {
          if (!row || !row[cols.route]) return null;
          const dateISO = toISO(row[cols.date]);
          if (!dateISO) return null;
          return {
            route_external_code: String(row[cols.route]),
            work_date:   dateISO,
            visits_plan: row[cols.plan] != null ? parseFloat(row[cols.plan]) : null,
            visits_fact: row[cols.fact] != null ? parseFloat(row[cols.fact]) : null,
            service_pct: row[cols.pct]  != null ? parseFloat(row[cols.pct])  : null,
          };
        }).filter(Boolean);

        resolve(result);
      } catch (err) {
        reject(new Error('Не удалось прочитать файл: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}


// ── ПАРСЕР ФАЙЛА "ЗАКАЗ" (широкий формат) ────────────────────────────────────
// Формат: колонка A — код маршрута (1054-00001), B — "Значения" (Кол-во тт),
// далее по колонке на каждый день месяца (заголовок = дата). Значение = заказ визитов.
// Возвращает [{ route_code, work_date, visits }] — по одной записи на непустую ячейку.
export function parseOrderFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (!rows.length) { resolve([]); return; }

        const toISO = excelToISO;

        const header = rows[0] || [];

        // Колонка с внутренним кодом маршрута: ищем заголовок ровно «Маршрут»
        // (не «Регион/Область/Город/Наименование маршрута»). Нет — колонка A (старый формат).
        let codeCol = header.findIndex(h => String(h || '').trim().toLowerCase() === 'маршрут');
        if (codeCol < 0) codeCol = 0;

        // Колонки-даты: любой заголовок, разбираемый как дата (настоящие даты
        // Excel или текст). Так ловим и старый формат (даты с колонки C), и новый
        // (метаданные слева, даты правее).
        const dateCols = [];
        for (let c = 0; c < header.length; c++) {
          if (c === codeCol) continue;
          const iso = toISO(header[c]);
          if (iso) dateCols.push({ c, iso });
        }
        if (!dateCols.length) { reject(new Error('Не найдены колонки-даты в шапке файла заказа')); return; }

        // Строки могут идти по точкам (несколько на маршрут) — суммируем визиты
        // всех точек маршрута за день в заказ по маршруту (importOrders по
        // маршрут+дата перезаписывает, поэтому сворачиваем здесь).
        const agg = new Map(); // `${code}__${iso}` → visits
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i] || [];
          const code = String(row[codeCol] || '').trim();
          if (!code) continue;
          for (const { c, iso } of dateCols) {
            const n = parseInt(row[c], 10);
            if (!Number.isFinite(n) || n <= 0) continue;
            const key = `${code}__${iso}`;
            agg.set(key, (agg.get(key) || 0) + n);
          }
        }
        const result = [];
        for (const [key, visits] of agg) {
          const idx = key.lastIndexOf('__');
          result.push({ route_code: key.slice(0, idx), work_date: key.slice(idx + 2), visits });
        }
        resolve(result);
      } catch (err) {
        reject(new Error('Не удалось прочитать файл заказа: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}

// ── ПАРСЕР ФАЙЛА "ГИППЕРА · ПЛАН/ЗАКАЗ" (широкий календарь) ───────────────────
// Колонки: A внутр.код маршрута, B внеш.код клиента (ship_to), Клиент, Адрес,
// Долгота, Широта, Время визита, Частота визитов, Маршрут, далее колонки-даты (1 = визит запланирован).
// Возвращает { points:[{route_code, ship_to, client, address, lat, lon, visit_time, frequency}],
//              plan:[{route_code, ship_to, work_date}] }
export function parseHyperPlanFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (!rows.length) { resolve({ points: [], plan: [] }); return; }

        const toISO = excelToISO;
        // Ищем строку заголовков (первые 5 строк) — где есть «внутренний код маршрута»
        let hRow = 0;
        for (let i = 0; i < Math.min(5, rows.length); i++) {
          const r = (rows[i] || []).map(v => String(v || '').toLowerCase());
          if (r.some(v => v.includes('внутренн') && v.includes('код'))) { hRow = i; break; }
        }
        const header = (rows[hRow] || []).map(v => String(v || '').trim());
        const hl = header.map(v => v.toLowerCase());
        // Колонки по названию заголовка (не по позиции — слева могут быть регион/BU и пр.)
        const find = (test) => hl.findIndex(test);
        // Код маршрута: «внутренний код маршрута», либо колонка ровно «Маршрут»
        // (не «Наименование/Регион/Область/Город маршрута»).
        let cRoute  = find(h => h.includes('внутренн') && h.includes('код'));
        if (cRoute === -1) cRoute = find(h => h === 'маршрут');
        const cShip   = find(h => (h.includes('внешн') && h.includes('код') && h.includes('клиент')) || h === 'ship_to');
        const cClient = find(h => h === 'клиент' || (h.includes('клиент') && !h.includes('код')));
        const cAddr   = find(h => h.includes('адрес'));
        const cLon    = find(h => h.includes('долгот'));
        const cLat    = find(h => h.includes('широт'));
        const cTime   = find(h => h.includes('время визит'));
        const cFreq   = find(h => h.includes('частот'));
        if (cRoute === -1 || cShip === -1) {
          reject(new Error('Не найдены колонки «Внутренний код маршрута»/«Внешний код клиента». Заголовки файла: ' + header.filter(Boolean).slice(0, 20).join(' | ')));
          return;
        }
        const dateCols = [];
        for (let c = 0; c < (rows[hRow] || []).length; c++) { const iso = toISO(rows[hRow][c]); if (iso) dateCols.push({ c, iso }); }
        if (!dateCols.length) { reject(new Error('Не найдены колонки-даты в план-файле гипперов')); return; }

        const points = [];
        const plan = [];
        const seen = new Set();
        for (let i = hRow + 1; i < rows.length; i++) {
          const row = rows[i] || [];
          const routeCode = String(row[cRoute] || '').trim();
          const shipTo    = String(row[cShip] || '').trim();
          if (!routeCode || !shipTo) continue;
          const key = routeCode + '||' + shipTo;
          if (!seen.has(key)) {
            seen.add(key);
            points.push({
              route_code: routeCode, ship_to: shipTo,
              client:  cClient !== -1 && row[cClient] != null ? String(row[cClient]) : '',
              address: cAddr   !== -1 && row[cAddr]   != null ? String(row[cAddr])   : '',
              lon: cLon !== -1 && row[cLon] != null && row[cLon] !== '' ? parseFloat(row[cLon]) : null,
              lat: cLat !== -1 && row[cLat] != null && row[cLat] !== '' ? parseFloat(row[cLat]) : null,
              visit_time: cTime !== -1 && row[cTime] != null && row[cTime] !== '' ? parseInt(row[cTime],10) : null,
              frequency:  cFreq !== -1 && row[cFreq] != null && row[cFreq] !== '' ? parseInt(row[cFreq],10) : null,
            });
          }
          for (const { c, iso } of dateCols) {
            const v = row[c];
            if (v == null || v === '') continue;
            const n = parseInt(v, 10);
            if (!Number.isFinite(n) || n <= 0) continue;
            plan.push({ route_code: routeCode, ship_to: shipTo, work_date: iso });
          }
        }
        resolve({ points, plan });
      } catch (err) { reject(new Error('Не удалось прочитать план-файл гипперов: ' + err.message)); }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}

// ── ПАРСЕР ФАЙЛА "ГИППЕРА · ФАКТ" (длинный, из BI) ───────────────────────────
// Шапка-фильтры сверху, строка заголовков содержит SHIP_TO:
// TM Name, SV Name, SHIP_TO, CLIENT, ADDRESS, Date, Visits Plan, Visits Fact, Visits Service%
// Возвращает [{ sv_code, ship_to, work_date, fact }]
export function parseHyperFactFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        let headerRow = -1;
        for (let i = 0; i < Math.min(10, rows.length); i++) {
          const r = (rows[i] || []).map(v => String(v || ''));
          if (r.some(v => v.includes('SHIP_TO'))) { headerRow = i; break; }
        }
        if (headerRow === -1) { reject(new Error('Не найдена строка заголовков (SHIP_TO) в факт-файле гипперов')); return; }
        const hdrs = rows[headerRow].map(h => String(h || '').trim());
        const idx = (names) => hdrs.findIndex(h => names.some(n => h.toLowerCase() === n.toLowerCase() || h.toLowerCase().includes(n.toLowerCase())));
        const cols = { sv: idx(['SV Name']), ship: idx(['SHIP_TO']), date: idx(['Date']), fact: idx(['Visits Fact']) };
        const toISO = excelToISO;
        const result = [];
        for (let i = headerRow + 1; i < rows.length; i++) {
          const row = rows[i] || [];
          const ship = String(row[cols.ship] || '').trim();
          const dateISO = toISO(row[cols.date]);
          if (!ship || !dateISO) continue;
          result.push({
            sv_code: String(row[cols.sv] || '').trim(),
            ship_to: ship,
            work_date: dateISO,
            fact: row[cols.fact] != null && row[cols.fact] !== '' ? parseInt(row[cols.fact], 10) : 0,
          });
        }
        resolve(result);
      } catch (err) { reject(new Error('Не удалось прочитать факт-файл гипперов: ' + err.message)); }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}

// ── ПАРСЕР ФАЙЛА "ГИППЕРА" (точки внутри маршрута) ───────────────────────────
// Формат: TM Name, SV Name, ROUTE_NAME, SHIP_TO, CLIENT, Адрес, ПРИОРИТЕТ МАРШРУТА, Сцеп

export function parseHyperFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (rows.length < 2) { resolve([]); return; }

        const hdrs = rows[0].map(h => String(h || '').trim());
        const idx = (names) => hdrs.findIndex(h => names.some(n => h.toLowerCase().includes(n.toLowerCase())));

        const cols = {
          route:    idx(['ROUTE_NAME']),
          shipTo:   idx(['SHIP_TO']),
          client:   idx(['CLIENT']),
          address:  idx(['Адрес']),
          priority: idx(['ПРИОРИТЕТ']),
          concat:   idx(['Сцеп']),
        };

        const result = rows.slice(1).map(row => {
          if (!row || !row[cols.route] || !row[cols.shipTo]) return null;
          return {
            route_external_code: String(row[cols.route]),
            ship_to:  String(row[cols.shipTo]),
            client:   row[cols.client]   ? String(row[cols.client])   : '',
            address:  row[cols.address]  ? String(row[cols.address])  : '',
            priority: row[cols.priority] ? String(row[cols.priority]) : '',
            concat_key: row[cols.concat] ? String(row[cols.concat])   : '',
          };
        }).filter(Boolean);

        resolve(result);
      } catch (err) {
        reject(new Error('Не удалось прочитать файл: ' + err.message));
      }
    };
    reader.onerror = () => reject(new Error('Ошибка чтения файла'));
    reader.readAsBinaryString(file);
  });
}


// ── ВЫГРУЗКА УЧЁТОК ────────────────────────────────────────────────────────────

export function exportUsersToExcel(users, fileName = 'Учётки') {
  const wb = XLSX.utils.book_new();
  const ROLE_LABELS = { sv: 'СВ', tm: 'ТМ', rm: 'РМ', admin: 'Admin' };

  const data = [
    ['ФИО', 'Код для входа', 'Роль', 'ТМ'],
    ...users.map(u => [u.full_name, u.login, ROLE_LABELS[u.role] || u.role, u.tm_name || '']),
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 28 }, { wch: 22 }, { wch: 10 }, { wch: 24 }];

  // Стиль заголовка
  for (let c = 0; c < 4; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) {
      ws[addr].s = {
        fill: { fgColor: { rgb: '1F3864' }, patternType: 'solid' },
        font: { color: { rgb: 'FFFFFF' }, bold: true },
      };
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, 'Учётки');
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}
